import Styles from "features/configuration/common/styles";
import React from "react";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import styled, { css } from "styled-components";
import { formatDate } from "@shared/utils/date";
import AntRetweetOutlined from "@ant-design/icons/RetweetOutlined";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import {
  ClientData,
  ClientStatus,
  LanguageNameSpaces,
  UsageType,
} from "@shared/types";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { displayResultMessage } from "utils/misc";
import WhitelistTag from "features/configuration/accounts/AccountConfigurationDrawer/WhitelistTag";
import AccountStatusTag from "features/configuration/accounts/AccountConfigurationDrawer/AccountStatusTag";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { useChangeAccountStatus } from "@shared/features/configuration/hooks";
import {
  useClientsData,
  useExchangesMetadata,
  useIsSubUser,
} from "@shared/utils/hooks/misc";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { phoneMinWidth } from "common/NewLook/layout.ts";

interface AccountInfoProps {
  account: ClientData;
}

const AccountInfo = ({ account }: AccountInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { getAccountHasActiveDuplicate } = useClientsData();
  const { isLoading: isLoadingChangeAccountStatus, handleChangeClientStatus } =
    useChangeAccountStatus(undefined, displayResultMessage);
  const { quotaModal, handleTriggerClick } = useQuotaModal({
    usageType: UsageType.CLIENT,
  });
  const isSubUser = useIsSubUser();
  const { getIsFree } = useExchangesMetadata();

  const revalidateAccount = () => {
    handleChangeClientStatus([account.client_id], ClientStatus.ACTIVE, true);
  };

  const handleRevalidateClick = () => {
    if (getIsFree(account.exchange)) {
      revalidateAccount();
    } else {
      handleTriggerClick({
        ignoreQuotaCheck: getAccountHasActiveDuplicate(account.client_id),
      })
        .then((isQuotaError) => {
          if (!isQuotaError) {
            revalidateAccount();
          }
        })
        .catch(() => {});
    }
  };

  const revalidateButton = (account.client_status === ClientStatus.INVALID ||
    account.has_assigned_ips === false) &&
    !isSubUser && (
      <Tooltip title={t("account_info.re_validate_api_keys")}>
        <div id="revalidateAPIs">
          <RetweetOutlined onClick={handleRevalidateClick} />
        </div>
      </Tooltip>
    );

  const getStatusTag = () => {
    const showWhitelistTag =
      account.has_assigned_ips === false && account.is_valid;
    const isMobileWithIPsTag = isMobile && showWhitelistTag;
    return (
      <Flex
        style={{
          width: "fit-content",
          flexDirection: isMobileWithIPsTag ? "column" : "row",
          alignItems: isMobileWithIPsTag ? "flex-start" : "center",
        }}
      >
        <AccountStatusTag status={account.client_status} />
        <Flex>
          {showWhitelistTag && (
            <WhitelistTag
              withBgColor={true}
              exchange={account.exchange}
              style={{
                marginLeft: isMobileWithIPsTag ? 0 : 6,
                marginTop: isMobileWithIPsTag ? 4 : 0,
              }}
            />
          )}
          {isMobileWithIPsTag && revalidateButton}
        </Flex>
        {!isMobileWithIPsTag && revalidateButton}
      </Flex>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoadingChangeAccountStatus}
      spinnerMarginTop={100}
    >
      <InfoWrapper style={{ maxWidth: "100%" }}>
        <Styles.CardsTitle
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          {t("account_info.title")}
        </Styles.CardsTitle>
        <StyledCard>
          <Text type="secondary">{t("account_info.name")}</Text>
          <Text>{account.client_name}</Text>
        </StyledCard>
        <StyledCard>
          <Text type="secondary">{t("account_info.exchange")}</Text>
          <Text as="div">
            <TextWithIcon
              text={account.exchange}
              coinOrExchangeName={account.exchange}
              iconType={ICON_TYPES.EXCHANGE}
              iconSize={"l"}
            />
          </Text>
        </StyledCard>
        <StyledCard>
          <Text type="secondary">{t("account_info.api_key")}</Text>
          <Text>{account.api_key}</Text>
        </StyledCard>
        <StyledCard>
          <Text type="secondary">{t("account_info.creation_date")}</Text>
          <Text>{formatDate(account.creation_date, true, true)}</Text>
        </StyledCard>
        <StyledCard>
          <Text type="secondary">{t("account_info.status")}</Text>
          {getStatusTag()}
        </StyledCard>
      </InfoWrapper>
      {quotaModal}
    </LoadingSpinnerOverlay>
  );
};

export default AccountInfo;

// The drawer's lead: the account's identity.
const InfoWrapper = styled(Styles.CardsWrapper)``;
const StyledCard = styled(Styles.Card)`
  height: auto;
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 16px;
      min-height: 52px;
      padding: 12px 24px;
    `}
`;
const RetweetOutlined = styled(AntRetweetOutlined)`
  cursor: pointer;
  font-size: ${(p) => p.theme.typography.fontSizes.XL};
  margin-right: 8px;
  margin-left: 8px;
  color: ${(p) => p.theme.colors.primaryGrey};
  @media (min-width: ${phoneMinWidth}) {
    &:hover {
      color: ${(p) => p.theme.colors.primaryBlue};
    }
  }
`;
